import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SecurityService } from 'src/app/services/security/security.service';

@Component({
  selector: 'app-password-recovery',
  templateUrl: './password-recovery.component.html',
  styleUrls: ['./password-recovery.component.css']
})
export class PasswordRecoveryComponent implements OnInit {

  @Output() onCancel = new EventEmitter<void>();
  @Output() onSuccess = new EventEmitter<void>();

  // Control de pasos
  step: 'username' | 'select-method' | 'verify-code' | 'new-password' = 'username';
  
  // Datos del flujo
  username = '';
  tempToken = '';
  availableMethods: string[] = [];
  configuredMethods: any = {};
  selectedMethod: 'sms' | 'authenticator' = 'sms';
  phoneNumber: string | null = null;
  verificationCode = '';
  newPassword = '';
  confirmPassword = '';
  
  // Estados
  loading = false;
  errors: any[] = [];

  constructor(
    private authService: AuthService,
    private securityService: SecurityService
  ) {}

  ngOnInit(): void {}

  // Paso 1: Enviar username
  async submitUsername(): Promise<void> {
    if (!this.username || this.username.length < 3 || this.loading) return;

    this.loading = true;
    this.errors = [];

    try {
      // Llamar endpoint de recuperación
      const resp = await this.authService.initiatePasswordRecovery(this.username);
      
      if (resp.ok) {
        this.tempToken = resp.tempToken;
        this.availableMethods = resp.availableMethods || [];
        this.configuredMethods = resp.configuredMethods || {};
        this.phoneNumber = resp.phone || null;
        
        // Ir a selector de método
        this.step = 'select-method';
      }
    } catch (error: any) {
      this.errors = [{ msg: error?.error?.msg || 'Error al iniciar recuperación de contraseña' }];
    } finally {
      this.loading = false;
    }
  }

  // Paso 2: Método seleccionado (evento del componente reutilizado)
  onMethodSelected(method: string): void {
    this.selectedMethod = method as 'sms' | 'authenticator';
    this.step = 'verify-code';
  }

  // Paso 3: Verificar código ANTES de continuar a nueva contraseña
  async verifyCodeAndContinue(): Promise<void> {
    if (!this.verificationCode || this.verificationCode.length !== 6 || this.loading) return;
    
    this.loading = true;
    this.errors = [];

    try {
      // Verificar el código 2FA usando el endpoint específico de recuperación
      const resp = await this.authService.verifyRecoveryCode(
        this.tempToken,
        this.verificationCode,
        this.selectedMethod
      );
      
      if (resp.ok) {
        // Código verificado correctamente, pasar a nueva contraseña
        this.step = 'new-password';
      }
    } catch (error: any) {
      this.errors = [{ msg: error?.error?.msg || 'Código inválido o expirado' }];
    } finally {
      this.loading = false;
    }
  }

  // Paso 4: Cambiar contraseña (ahora incluye verificación 2FA)
  async submitNewPassword(): Promise<void> {
    if (!this.isPasswordValid() || this.loading) return;

    this.loading = true;
    this.errors = [];

    try {
      const resp = await this.authService.resetPasswordWith2FA(
        this.tempToken,
        this.newPassword,
        this.verificationCode,
        this.selectedMethod
      );

      if (resp.ok) {
        this.onSuccess.emit();
      }
    } catch (error: any) {
      this.errors = [{ msg: error?.error?.msg || 'Error al cambiar contraseña' }];
    } finally {
      this.loading = false;
    }
  }

  // Validación de contraseña
  isPasswordValid(): boolean {
    return this.passwordHasMinLength && 
           this.passwordHasMaxLength &&
           this.passwordHasLowercase &&
           this.passwordHasUppercase && 
           this.passwordHasNumber && 
           this.passwordHasSpecialChar && 
           this.passwordsMatch;
  }

  cancel(): void {
    this.onCancel.emit();
  }

  // Helpers para validación de contraseña
  get passwordHasMinLength(): boolean {
    return this.newPassword.length >= 6;
  }

  get passwordHasMaxLength(): boolean {
    return this.newPassword.length <= 12;
  }

  get passwordHasLowercase(): boolean {
    return /[a-z]/.test(this.newPassword);
  }

  get passwordHasUppercase(): boolean {
    return /[A-Z]/.test(this.newPassword);
  }

  get passwordHasNumber(): boolean {
    return /\d/.test(this.newPassword);
  }

  get passwordHasSpecialChar(): boolean {
    return /[@$!%*?&.#\-_,;:+=()\[\]{}|~]/.test(this.newPassword);
  }

  get passwordsMatch(): boolean {
    return this.newPassword === this.confirmPassword && this.newPassword !== '';
  }

  // Propiedades para mostrar/ocultar contraseña
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }
}
